import { useEffect, useState, type ReactNode } from "react";

import { useUserSheet } from "./userSheetContext";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { SIDEBAR_WIDTH, useSidebar } from "@/components/ui/sidebar";

import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { FLOATING_PROMPT_SELECTOR } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";

// The sheet frame: the Radix overlay and the width that widens when the comms
// rail opens. Hosts that already own a dialog (the application review portal)
// skip this block and compose UserSheetBody directly.
export const UserSheetFrame = ({
  children,
  fallbackName,
  onClose,
}: {
  children: ReactNode;
  // Shown until the profile resolves, so the title doesn't flash empty.
  fallbackName?: string;
  onClose: () => void;
}) => {
  /* React Hooks */
  const { isCommunicationsOpen, jobId, workerId } = useUserSheet();
  const { open: isSidebarOpen } = useSidebar();
  // Once the rail has opened, preserve the wider frame when it collapses.
  const [hasOpenedCommunications, setHasOpenedCommunications] =
    useState(isCommunicationsOpen);
  useEffect(() => {
    if (isCommunicationsOpen) setHasOpenedCommunications(true);
  }, [isCommunicationsOpen]);

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });

  /* Helpers */
  const user = userApi.data;
  const sheetWidth = hasOpenedCommunications
    ? `calc(100% - ${isSidebarOpen ? SIDEBAR_WIDTH : "48px"})`
    : "65%";
  const title = formatting.fullName(
    user?.profile.firstName,
    user?.profile.lastName,
    fallbackName ?? "Candidate",
  );

  /* Render */
  const render = () => {
    return (
      <Sheet
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        open
      >
        <SheetContent
          className={cn(
            "flex flex-col gap-0 overflow-hidden p-0 transition-[width,max-width] duration-500 ease-out dark:bg-[#212327] sm:max-w-none",
            hasOpenedCommunications ? "!min-w-[1000px]" : "!min-w-0",
          )}
          hideClose
          // Don't close the sheet when interacting with the active screen prompt
          // Ex: notis, screening in progresss
          onInteractOutside={(e) => {
            const target = e.detail.originalEvent.target as HTMLElement | null;
            if (target?.closest(FLOATING_PROMPT_SELECTOR)) {
              e.preventDefault();
            }
          }}
          overlayClassName="bg-black/30"
          style={{ maxWidth: sheetWidth, width: sheetWidth }}
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <SheetDescription className="sr-only">
            User profile and activity
          </SheetDescription>
          {children}
        </SheetContent>
      </Sheet>
    );
  };

  return render();
};
